import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, RotateCcw, Play, Zap, Star, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const VALUES = ["IMPACT", "VALUE", "CHANGE", "VISION", "PURPOSE"];
const OBSTACLES = ["STAGNATION", "MEDIOCRITY", "DOUBT"];

const VisionaryPathGame = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = useRef();
  
  // Game Refs
  const mousePos = useRef({ x: 0, y: 0 });
  const orb = useRef({ x: 400, y: 400, radius: 10 });
  const objects = useRef([]);
  const frameCount = useRef(0);
  const particles = useRef([]);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Refs for dynamic dimensions
  const canvasWidth = useRef(800);
  const canvasHeight = useRef(500);

  useEffect(() => {
    const saved = localStorage.getItem("meraki_visionary_high_score");
    if (saved) setHighScore(parseInt(saved));
    
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        if (canvasRef.current) {
          canvasRef.current.width = canvasWidth.current;
          canvasRef.current.height = canvasHeight.current;
        }
        if (orb.current.x > canvasWidth.current) {
          orb.current.x = canvasWidth.current / 2;
        }
        orb.current.y = canvasHeight.current - 80;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = containerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const initGame = () => {
    objects.current = [];
    particles.current = [];
    orb.current = { x: canvasWidth.current / 2, y: canvasHeight.current - 80, targetX: canvasWidth.current / 2, radius: 12 };
    setScore(0);
    setGameState("playing");
    frameCount.current = 0;
    mousePos.current = { x: canvasWidth.current / 2 };
  };

  const createParticles = (x, y, color, count = 15) => {
    for (let i = 0; i < count; i++) {
      particles.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 6,
        vy: (Math.random() - 0.5) * 6,
        size: Math.random() * 3 + 1,
        life: 1.0,
        color
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current || isTouchDevice) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) * window.devicePixelRatio;
    orb.current.targetX = x;
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = (touch.clientX - rect.left) * window.devicePixelRatio;
    orb.current.targetX = x;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const update = () => {
      if (gameState !== "playing") return;

      frameCount.current++;

      // Smooth Orb Follow
      orb.current.x += (orb.current.targetX - orb.current.x) * 0.15;

      // Spawn Objects
      if (frameCount.current % Math.max(30, 70 - Math.floor(score / 5)) === 0) {
        const isObstacle = Math.random() > 0.4;
        const text = isObstacle 
          ? OBSTACLES[Math.floor(Math.random() * OBSTACLES.length)]
          : VALUES[Math.floor(Math.random() * VALUES.length)];
        
        objects.current.push({
          x: Math.random() * (canvasWidth.current - 100) + 50,
          y: -50,
          text,
          speed: 2.5 + (score * 0.08),
          type: isObstacle ? "obstacle" : "value",
          width: ctx.measureText(text).width + 20
        });
      }

      // Update Objects
      objects.current.forEach((obj, index) => {
        obj.y += obj.speed;

        // Collision Detection
        const dx = orb.current.x - obj.x;
        const dy = orb.current.y - obj.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 40) {
          if (obj.type === "value") {
            setScore(s => s + 10);
            createParticles(obj.x, obj.y, "#c9a961");
            objects.current.splice(index, 1);
          } else {
            setGameState("gameOver");
            createParticles(orb.current.x, orb.current.y, "#ef4444", 30);
          }
        }

        // Remove off-screen
        if (obj.y > canvasHeight.current + 50) {
          objects.current.splice(index, 1);
        }
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);
      
      // Draw Atmospheric Background
      const grad = ctx.createLinearGradient(0, 0, 0, canvasHeight.current);
      grad.addColorStop(0, "#050505");
      grad.addColorStop(1, "#0a0908");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvasWidth.current, canvasHeight.current);

      // Draw Starfield (Background)
      ctx.fillStyle = "rgba(255,255,255,0.1)";
      for(let i=0; i<50; i++) {
        const x = (Math.sin(i + frameCount.current * 0.001) * 0.5 + 0.5) * canvasWidth.current;
        const y = (Math.cos(i * 1.5 + frameCount.current * 0.002) * 0.5 + 0.5) * canvasHeight.current;
        ctx.beginPath(); ctx.arc(x, y, 1, 0, Math.PI*2); ctx.fill();
      }

      // Draw Objects
      objects.current.forEach(obj => {
        ctx.font = "10px Impact, sans-serif";
        ctx.textAlign = "center";
        
        if (obj.type === "value") {
          ctx.fillStyle = "#c9a961";
          ctx.shadowBlur = 15;
          ctx.shadowColor = "#c9a961";
          ctx.fillText(obj.text, obj.x, obj.y);
          ctx.beginPath();
          ctx.arc(obj.x, obj.y - 4, 15, 0, Math.PI*2);
          ctx.strokeStyle = "rgba(201, 169, 97, 0.2)";
          ctx.stroke();
        } else {
          ctx.fillStyle = "#ef4444";
          ctx.shadowBlur = 10;
          ctx.shadowColor = "#ef4444";
          ctx.fillText(obj.text, obj.x, obj.y);
          ctx.lineWidth = 1;
          ctx.strokeStyle = "rgba(239, 68, 68, 0.3)";
          ctx.strokeRect(obj.x - 40, obj.y - 15, 80, 25);
        }
        ctx.shadowBlur = 0;
      });

      // Draw Orb (The Vision)
      if (gameState === "playing") {
        ctx.save();
        ctx.translate(orb.current.x, orb.current.y);
        
        const pulse = 1 + Math.sin(frameCount.current * 0.1) * 0.2;
        const orbGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, orb.current.radius * pulse * 2);
        orbGrad.addColorStop(0, "#c9a961");
        orbGrad.addColorStop(1, "transparent");
        ctx.fillStyle = orbGrad;
        ctx.beginPath();
        ctx.arc(0, 0, orb.current.radius * pulse * 2, 0, Math.PI*2);
        ctx.fill();

        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(0, 0, orb.current.radius, 0, Math.PI*2);
        ctx.fill();
        ctx.restore();
      }

      // Draw Particles
      particles.current.forEach((p, i) => {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.02;
      });
      particles.current = particles.current.filter(p => p.life > 0);
      ctx.globalAlpha = 1.0;

      if (gameState === "playing") {
        requestRef.current = requestAnimationFrame(() => {
          update();
          draw();
        });
      } else {
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    requestRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, score, isTouchDevice]);

  useEffect(() => {
    if (gameState === "gameOver" && score > highScore) {
      setHighScore(score);
      localStorage.setItem("meraki_visionary_high_score", score.toString());
    }
  }, [gameState, score, highScore]);

  return (
    <section className="py-24 relative overflow-hidden bg-[#050505]">
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="PHL-01" label="Interactive Philosophy" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Visionary <span className="italic gold-text">Path</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-[10px] md:text-sm uppercase tracking-[0.2em] max-w-lg mx-auto leading-relaxed">
              Navigate through the noise. Collect core values and pioneer visible change.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative max-w-4xl mx-auto aspect-[3/4] md:aspect-video rounded-[40px] overflow-hidden border border-[var(--rule)] bg-[#050505] shadow-[0_0_100px_rgba(0,0,0,0.5)] group"
          data-lenis-prevent
          onMouseMove={handleMouseMove}
          onTouchMove={(e) => { e.stopPropagation(); handleTouchMove(e); }}
          onWheel={(e) => e.stopPropagation()}
          onClick={() => gameState === "start" && initGame()}
        >
          <canvas 
            ref={canvasRef}
            width={800}
            height={500}
            className="w-full h-full object-cover"
          />

          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/images/visionary_path_thumb.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/80 backdrop-blur-[6px]" />

                <div className="relative z-20 flex flex-col items-center p-8 md:p-12 rounded-3xl bg-black/40 backdrop-blur-xl max-w-[85%] md:max-w-none">
                  <Star className="text-[var(--gold)] w-10 h-10 md:w-16 md:h-16 mb-6 md:mb-8 animate-pulse" />
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-4 italic text-center leading-tight">Pioneer the Future?</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-[0.3em] uppercase mb-8 md:mb-10 text-center leading-relaxed">
                    {isTouchDevice ? "Drag to navigate." : "Move mouse to navigate."} <br /> Avoid the noise, collect the vision.
                  </p>
                  <button 
                    className="btn-primary px-8 md:px-10 py-4" 
                    onClick={(e) => { e.stopPropagation(); initGame(); }}
                  >
                    Enter Path
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 backdrop-blur-2xl z-30 p-6 text-center"
              >
                <Zap className="text-red-500 w-12 h-12 md:w-20 md:h-20 mb-4 md:mb-6" />
                <h3 className="font-display text-3xl md:text-5xl text-white mb-2 tracking-tighter uppercase leading-tight">Vision Obscured</h3>
                <p className="text-red-400 font-impact text-[10px] md:text-sm tracking-[0.4em] uppercase mb-8 md:mb-12">Lost in the noise of mediocrity</p>
                
                <div className="flex gap-8 md:gap-16 mb-10 md:mb-12">
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">IMPACT CREATED</div>
                    <div className="text-3xl md:text-5xl font-display text-white">{score}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[8px] md:text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">RECORD</div>
                    <div className="text-3xl md:text-5xl font-display text-[var(--gold)]">{highScore}</div>
                  </div>
                </div>

                <button className="btn-primary px-8 md:px-12 py-4 md:py-5" onClick={(e) => { e.stopPropagation(); initGame(); }} data-cursor="Retry">
                  <RotateCcw className="w-4 h-4 mr-2" /> Re-ignite Vision
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* HUD */}
          {gameState === "playing" && (
            <div className="absolute top-10 right-10 text-right z-20 pointer-events-none">
              <div className="text-[10px] text-[var(--gold)] uppercase tracking-[0.3em] mb-1">VISIBLE CHANGE</div>
              <div className="text-3xl font-display text-white">{score}</div>
            </div>
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Visionary Path"
        instructions={[
          { title: "Navigate the Light", desc: "You control the glowing orb of Vision. Move your mouse left and right to navigate through the atmospheric void." },
          { title: "Collect Values", desc: "Look for golden terms like 'IMPACT', 'VALUE', and 'VISION'. Passing through these will increase your 'Visible Change' score." },
          { title: "Avoid the Noise", desc: "Red terms like 'STAGNATION' and 'MEDIOCRITY' represent the noise of the market. Touching them will obscure your vision and end the journey." },
          { title: "Scale the Future", desc: "The path moves faster as your impact grows. Stay focused on the light to reach your peak potential." }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default VisionaryPathGame;
